/**
 * District Types
 *
 * City district structure for pre-built campaign settings.
 * Each district has atmosphere data (lighting, sounds, smell) that
 * feeds into AI narration for environmental descriptions.
 */
import { z } from 'zod';
// =============================================================================
// LANDMARK
// =============================================================================
export const LandmarkTypeSchema = z.enum([
    'tavern', 'temple', 'market', 'ruin', 'workshop',
    'library', 'fortress', 'monument', 'gate', 'garden',
    'arena', 'dock', 'tower', 'crypt', 'plaza',
]);
export const LandmarkSchema = z.object({
    name: z.string(),
    type: LandmarkTypeSchema,
    description: z.string(),
    npcIds: z.array(z.string()).default([]),
});
// =============================================================================
// ATMOSPHERE
// =============================================================================
export const AtmosphereSchema = z.object({
    lighting: z.string(), // "perpetual twilight", "bioluminescent glow"
    sounds: z.string(), // "chains rattling", "distant bells"
    smell: z.string(), // "ozone and old paper", "forge smoke"
});
// =============================================================================
// DISTRICT
// =============================================================================
export const DistrictSchema = z.object({
    id: z.string(),
    name: z.string(),
    epithet: z.string(), // "The Breathing District", "The Quiet"
    description: z.string(), // 2-3 sentence overview
    mood: z.string(), // One-line atmosphere summary
    dominantFaction: z.string().nullable(), // Faction ID or null if contested
    contestedBy: z.array(z.string()).default([]), // Faction IDs vying for control
    landmarks: z.array(LandmarkSchema).default([]),
    atmosphere: AtmosphereSchema,
    secrets: z.array(z.string()).default([]), // GM-only plot hooks
});
// =============================================================================
// CAMPAIGN WORLD (top-level container)
// =============================================================================
export const CampaignWorldSchema = z.object({
    id: z.string(),
    name: z.string(),
    epithet: z.string(), // "The Tethered City"
    description: z.string(),
    cosmology: z.string(), // How reality works in this world
    history: z.string(), // Key historical events
    currentCrisis: z.string(), // What's happening NOW
    districts: z.array(DistrictSchema),
});
// =============================================================================
// STARTING SCENARIO
// =============================================================================
export const StartingScenarioSchema = z.object({
    id: z.string(),
    title: z.string(),
    tagline: z.string(), // One-line hook
    description: z.string(), // 3-5 sentence setup
    involvedFactions: z.array(z.string()), // Faction IDs
    startingDistrict: z.string(), // District ID
    hooks: z.array(z.string()), // Initial plot threads
});
//# sourceMappingURL=district-types.js.map